import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Egg, 
  Calendar, 
  Save,
  Trash2,
  Package,
  RefreshCw,
  Edit2,
  Plus,
  Minus,
  AlertTriangle,
  Check,
  History,
  FileText,
  Download,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AdjustmentInputCard component - allows positive and negative values
const AdjustmentInputCard = ({ size, formData, handleInputChange, isEditing }) => (
  <div className={`rounded-xl border-2 ${size.color} p-4`}>
    <h4 className="font-semibold mb-3 flex items-center gap-2">
      <Egg className="w-4 h-4" />
      {size.label}
    </h4>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs opacity-70">Crates (+/-)</Label>
        <Input
          type="number"
          value={formData[`${size.key}_crates`]}
          onChange={(e) => handleInputChange(`${size.key}_crates`, e.target.value)}
          className={`mt-1 ${parseInt(formData[`${size.key}_crates`]) < 0 ? 'border-red-300 bg-red-50' : parseInt(formData[`${size.key}_crates`]) > 0 ? 'border-green-300 bg-green-50' : ''}`}
          disabled={!isEditing}
          data-testid={`input-${size.key}-crates`}
        />
      </div>
      <div>
        <Label className="text-xs opacity-70">Pieces (+/-)</Label>
        <Input
          type="number"
          value={formData[`${size.key}_pieces`]}
          onChange={(e) => handleInputChange(`${size.key}_pieces`, e.target.value)}
          className={`mt-1 ${parseInt(formData[`${size.key}_pieces`]) < 0 ? 'border-red-300 bg-red-50' : parseInt(formData[`${size.key}_pieces`]) > 0 ? 'border-green-300 bg-green-50' : ''}`}
          disabled={!isEditing}
          data-testid={`input-${size.key}-pieces`}
        />
      </div>
      {size.hasCrack && (
        <>
          <div>
            <Label className="text-xs text-orange-600">Crack Crates (+/-)</Label>
            <Input
              type="number"
              value={formData[`${size.key}_crack_crates`]}
              onChange={(e) => handleInputChange(`${size.key}_crack_crates`, e.target.value)}
              className={`mt-1 border-orange-200 ${parseInt(formData[`${size.key}_crack_crates`]) < 0 ? 'bg-red-50' : parseInt(formData[`${size.key}_crack_crates`]) > 0 ? 'bg-green-50' : ''}`}
              disabled={!isEditing}
              data-testid={`input-${size.key}-crack-crates`}
            />
          </div>
          <div>
            <Label className="text-xs text-orange-600">Crack Pieces (+/-)</Label>
            <Input
              type="number"
              value={formData[`${size.key}_crack_pieces`]}
              onChange={(e) => handleInputChange(`${size.key}_crack_pieces`, e.target.value)}
              className={`mt-1 border-orange-200 ${parseInt(formData[`${size.key}_crack_pieces`]) < 0 ? 'bg-red-50' : parseInt(formData[`${size.key}_crack_pieces`]) > 0 ? 'bg-green-50' : ''}`}
              disabled={!isEditing}
              data-testid={`input-${size.key}-crack-pieces`}
            />
          </div>
        </>
      )}
    </div>
  </div>
);

const ProductionsEdit = () => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPen, setSelectedPen] = useState('1');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingAdjustment, setExistingAdjustment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [allPenAdjustments, setAllPenAdjustments] = useState([]);
  const [editMode, setEditMode] = useState('pen'); // 'pen', 'summary', or 'history'
  
  // History state
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyStartDate, setHistoryStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [historyEndDate, setHistoryEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all', 'pen', 'summary'
  
  // Summary adjustment state
  const [summaryAdjustment, setSummaryAdjustment] = useState(null);
  const [isSummaryEditing, setIsSummaryEditing] = useState(false);
  const [summaryFormData, setSummaryFormData] = useState({
    extra_large_crates: '0',
    extra_large_pieces: '0',
    extra_large_crack_crates: '0',
    extra_large_crack_pieces: '0',
    large_crates: '0',
    large_pieces: '0',
    large_crack_crates: '0',
    large_crack_pieces: '0',
    medium_crates: '0',
    medium_pieces: '0',
    medium_crack_crates: '0',
    medium_crack_pieces: '0',
    small_crates: '0',
    small_pieces: '0',
    small_crack_crates: '0',
    small_crack_pieces: '0',
    damaged_crates: '0',
    damaged_pieces: '0',
    reason: ''
  });
  
  // Form data for selected pen adjustment
  const [formData, setFormData] = useState({
    extra_large_crates: '0',
    extra_large_pieces: '0',
    extra_large_crack_crates: '0',
    extra_large_crack_pieces: '0',
    large_crates: '0',
    large_pieces: '0',
    large_crack_crates: '0',
    large_crack_pieces: '0',
    medium_crates: '0',
    medium_pieces: '0',
    medium_crack_crates: '0',
    medium_crack_pieces: '0',
    small_crates: '0',
    small_pieces: '0',
    small_crack_crates: '0',
    small_crack_pieces: '0',
    damaged_crates: '0',
    damaged_pieces: '0',
    reason: ''
  });

  const pens = [1, 2, 3, 4, 5, 6];
  
  const eggSizes = [
    { key: 'extra_large', label: 'Extra Large', color: 'bg-purple-100 border-purple-300 text-purple-800', hasCrack: true },
    { key: 'large', label: 'Large', color: 'bg-blue-100 border-blue-300 text-blue-800', hasCrack: true },
    { key: 'medium', label: 'Medium', color: 'bg-green-100 border-green-300 text-green-800', hasCrack: true },
    { key: 'small', label: 'Small', color: 'bg-yellow-100 border-yellow-300 text-yellow-800', hasCrack: true },
    { key: 'damaged', label: 'Damaged', color: 'bg-red-100 border-red-300 text-red-800', hasCrack: false }
  ];

  useEffect(() => {
    if (editMode === 'pen') {
      fetchPenAdjustment();
    } else {
      fetchSummaryAdjustment();
    }
    fetchAllPenAdjustments();
  }, [selectedDate, selectedPen, editMode]);

  // Fetch history when in history mode
  useEffect(() => {
    if (editMode === 'history') {
      fetchAdjustmentHistory();
    }
  }, [editMode, historyStartDate, historyEndDate, historyFilter]);

  const fetchAdjustmentHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/production-adjustments/history?start_date=${historyStartDate}&end_date=${historyEndDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        let data = await response.json();
        
        // Apply filter
        if (historyFilter === 'pen') {
          data = data.filter(adj => adj.pen_number > 0);
        } else if (historyFilter === 'summary') {
          data = data.filter(adj => adj.pen_number === 0);
        }
        
        setHistoryData(data);
      }
    } catch (error) {
      console.error('Error fetching adjustment history:', error);
      toast.error('Failed to load adjustment history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchSummaryAdjustment = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/production-adjustments?date=${selectedDate}&pen_number=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const adjustment = data[0];
          setSummaryAdjustment(adjustment);
          setSummaryFormData({
            extra_large_crates: adjustment.extra_large_crates?.toString() || '0',
            extra_large_pieces: adjustment.extra_large_pieces?.toString() || '0',
            extra_large_crack_crates: adjustment.extra_large_crack_crates?.toString() || '0',
            extra_large_crack_pieces: adjustment.extra_large_crack_pieces?.toString() || '0',
            large_crates: adjustment.large_crates?.toString() || '0',
            large_pieces: adjustment.large_pieces?.toString() || '0',
            large_crack_crates: adjustment.large_crack_crates?.toString() || '0',
            large_crack_pieces: adjustment.large_crack_pieces?.toString() || '0',
            medium_crates: adjustment.medium_crates?.toString() || '0',
            medium_pieces: adjustment.medium_pieces?.toString() || '0',
            medium_crack_crates: adjustment.medium_crack_crates?.toString() || '0',
            medium_crack_pieces: adjustment.medium_crack_pieces?.toString() || '0',
            small_crates: adjustment.small_crates?.toString() || '0',
            small_pieces: adjustment.small_pieces?.toString() || '0',
            small_crack_crates: adjustment.small_crack_crates?.toString() || '0',
            small_crack_pieces: adjustment.small_crack_pieces?.toString() || '0',
            damaged_crates: adjustment.damaged_crates?.toString() || '0',
            damaged_pieces: adjustment.damaged_pieces?.toString() || '0',
            reason: adjustment.reason || ''
          });
          setIsSummaryEditing(false);
        } else {
          setSummaryAdjustment(null);
          resetSummaryForm();
          setIsSummaryEditing(true);
        }
      }
    } catch (error) {
      console.error('Error fetching summary adjustment:', error);
      toast.error('Failed to load summary adjustment');
    } finally {
      setLoading(false);
    }
  };

  const resetSummaryForm = () => {
    setSummaryFormData({
      extra_large_crates: '0',
      extra_large_pieces: '0',
      extra_large_crack_crates: '0',
      extra_large_crack_pieces: '0',
      large_crates: '0',
      large_pieces: '0',
      large_crack_crates: '0',
      large_crack_pieces: '0',
      medium_crates: '0',
      medium_pieces: '0',
      medium_crack_crates: '0',
      medium_crack_pieces: '0',
      small_crates: '0',
      small_pieces: '0',
      small_crack_crates: '0',
      small_crack_pieces: '0',
      damaged_crates: '0',
      damaged_pieces: '0',
      reason: ''
    });
  };

  const handleSummaryInputChange = (field, value) => {
    setSummaryFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSummary = async () => {
    const fields = [
      'extra_large_crates', 'extra_large_pieces', 'extra_large_crack_crates', 'extra_large_crack_pieces',
      'large_crates', 'large_pieces', 'large_crack_crates', 'large_crack_pieces',
      'medium_crates', 'medium_pieces', 'medium_crack_crates', 'medium_crack_pieces',
      'small_crates', 'small_pieces', 'small_crack_crates', 'small_crack_pieces',
      'damaged_crates', 'damaged_pieces'
    ];
    
    const hasNonZero = fields.some(f => parseInt(summaryFormData[f]) !== 0);
    if (!hasNonZero) {
      toast.error('Please enter at least one non-zero adjustment value');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: selectedDate,
        pen_number: 0, // 0 indicates summary/overall adjustment
        extra_large_crates: parseInt(summaryFormData.extra_large_crates) || 0,
        extra_large_pieces: parseInt(summaryFormData.extra_large_pieces) || 0,
        extra_large_crack_crates: parseInt(summaryFormData.extra_large_crack_crates) || 0,
        extra_large_crack_pieces: parseInt(summaryFormData.extra_large_crack_pieces) || 0,
        large_crates: parseInt(summaryFormData.large_crates) || 0,
        large_pieces: parseInt(summaryFormData.large_pieces) || 0,
        large_crack_crates: parseInt(summaryFormData.large_crack_crates) || 0,
        large_crack_pieces: parseInt(summaryFormData.large_crack_pieces) || 0,
        medium_crates: parseInt(summaryFormData.medium_crates) || 0,
        medium_pieces: parseInt(summaryFormData.medium_pieces) || 0,
        medium_crack_crates: parseInt(summaryFormData.medium_crack_crates) || 0,
        medium_crack_pieces: parseInt(summaryFormData.medium_crack_pieces) || 0,
        small_crates: parseInt(summaryFormData.small_crates) || 0,
        small_pieces: parseInt(summaryFormData.small_pieces) || 0,
        small_crack_crates: parseInt(summaryFormData.small_crack_crates) || 0,
        small_crack_pieces: parseInt(summaryFormData.small_crack_pieces) || 0,
        damaged_crates: parseInt(summaryFormData.damaged_crates) || 0,
        damaged_pieces: parseInt(summaryFormData.damaged_pieces) || 0,
        reason: summaryFormData.reason
      };

      const response = await fetch(`${API_URL}/api/production-adjustments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Summary adjustment saved - Inventory recalculated');
        fetchSummaryAdjustment();
        fetchAllPenAdjustments();
        setIsSummaryEditing(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save summary adjustment');
      }
    } catch (error) {
      console.error('Error saving summary adjustment:', error);
      toast.error('Failed to save summary adjustment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSummary = async () => {
    if (!summaryAdjustment) return;
    
    if (!window.confirm('Are you sure you want to delete this summary adjustment? This will recalculate the closing inventory.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/production-adjustments/${summaryAdjustment.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Summary adjustment deleted - Inventory recalculated');
        setSummaryAdjustment(null);
        resetSummaryForm();
        setIsSummaryEditing(true);
        fetchAllPenAdjustments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete summary adjustment');
      }
    } catch (error) {
      console.error('Error deleting summary adjustment:', error);
      toast.error('Failed to delete summary adjustment');
    }
  };

  const fetchPenAdjustment = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/production-adjustments?date=${selectedDate}&pen_number=${selectedPen}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const adjustment = data[0];
          setExistingAdjustment(adjustment);
          setFormData({
            extra_large_crates: adjustment.extra_large_crates?.toString() || '0',
            extra_large_pieces: adjustment.extra_large_pieces?.toString() || '0',
            extra_large_crack_crates: adjustment.extra_large_crack_crates?.toString() || '0',
            extra_large_crack_pieces: adjustment.extra_large_crack_pieces?.toString() || '0',
            large_crates: adjustment.large_crates?.toString() || '0',
            large_pieces: adjustment.large_pieces?.toString() || '0',
            large_crack_crates: adjustment.large_crack_crates?.toString() || '0',
            large_crack_pieces: adjustment.large_crack_pieces?.toString() || '0',
            medium_crates: adjustment.medium_crates?.toString() || '0',
            medium_pieces: adjustment.medium_pieces?.toString() || '0',
            medium_crack_crates: adjustment.medium_crack_crates?.toString() || '0',
            medium_crack_pieces: adjustment.medium_crack_pieces?.toString() || '0',
            small_crates: adjustment.small_crates?.toString() || '0',
            small_pieces: adjustment.small_pieces?.toString() || '0',
            small_crack_crates: adjustment.small_crack_crates?.toString() || '0',
            small_crack_pieces: adjustment.small_crack_pieces?.toString() || '0',
            damaged_crates: adjustment.damaged_crates?.toString() || '0',
            damaged_pieces: adjustment.damaged_pieces?.toString() || '0',
            reason: adjustment.reason || ''
          });
          setIsEditing(false);
        } else {
          setExistingAdjustment(null);
          resetForm();
          setIsEditing(true);
        }
      }
    } catch (error) {
      console.error('Error fetching adjustment:', error);
      toast.error('Failed to load adjustment');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPenAdjustments = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/production-adjustments?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllPenAdjustments(data);
      }
    } catch (error) {
      console.error('Error fetching all adjustments:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      extra_large_crates: '0',
      extra_large_pieces: '0',
      extra_large_crack_crates: '0',
      extra_large_crack_pieces: '0',
      large_crates: '0',
      large_pieces: '0',
      large_crack_crates: '0',
      large_crack_pieces: '0',
      medium_crates: '0',
      medium_pieces: '0',
      medium_crack_crates: '0',
      medium_crack_pieces: '0',
      small_crates: '0',
      small_pieces: '0',
      small_crack_crates: '0',
      small_crack_pieces: '0',
      damaged_crates: '0',
      damaged_pieces: '0',
      reason: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const hasNonZeroValues = () => {
    const fields = [
      'extra_large_crates', 'extra_large_pieces', 'extra_large_crack_crates', 'extra_large_crack_pieces',
      'large_crates', 'large_pieces', 'large_crack_crates', 'large_crack_pieces',
      'medium_crates', 'medium_pieces', 'medium_crack_crates', 'medium_crack_pieces',
      'small_crates', 'small_pieces', 'small_crack_crates', 'small_crack_pieces',
      'damaged_crates', 'damaged_pieces'
    ];
    return fields.some(f => parseInt(formData[f]) !== 0);
  };

  const handleSave = async () => {
    if (!hasNonZeroValues()) {
      toast.error('Please enter at least one non-zero adjustment value');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: selectedDate,
        pen_number: parseInt(selectedPen),
        extra_large_crates: parseInt(formData.extra_large_crates) || 0,
        extra_large_pieces: parseInt(formData.extra_large_pieces) || 0,
        extra_large_crack_crates: parseInt(formData.extra_large_crack_crates) || 0,
        extra_large_crack_pieces: parseInt(formData.extra_large_crack_pieces) || 0,
        large_crates: parseInt(formData.large_crates) || 0,
        large_pieces: parseInt(formData.large_pieces) || 0,
        large_crack_crates: parseInt(formData.large_crack_crates) || 0,
        large_crack_pieces: parseInt(formData.large_crack_pieces) || 0,
        medium_crates: parseInt(formData.medium_crates) || 0,
        medium_pieces: parseInt(formData.medium_pieces) || 0,
        medium_crack_crates: parseInt(formData.medium_crack_crates) || 0,
        medium_crack_pieces: parseInt(formData.medium_crack_pieces) || 0,
        small_crates: parseInt(formData.small_crates) || 0,
        small_pieces: parseInt(formData.small_pieces) || 0,
        small_crack_crates: parseInt(formData.small_crack_crates) || 0,
        small_crack_pieces: parseInt(formData.small_crack_pieces) || 0,
        damaged_crates: parseInt(formData.damaged_crates) || 0,
        damaged_pieces: parseInt(formData.damaged_pieces) || 0,
        reason: formData.reason
      };

      const response = await fetch(`${API_URL}/api/production-adjustments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(`Pen ${selectedPen} adjustment saved - Inventory recalculated`);
        fetchPenAdjustment();
        fetchAllPenAdjustments();
        setIsEditing(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save adjustment');
      }
    } catch (error) {
      console.error('Error saving adjustment:', error);
      toast.error('Failed to save adjustment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingAdjustment) return;
    
    if (!window.confirm('Are you sure you want to delete this adjustment? This will recalculate the closing inventory.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/production-adjustments/${existingAdjustment.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Adjustment deleted - Inventory recalculated');
        setExistingAdjustment(null);
        resetForm();
        setIsEditing(true);
        fetchAllPenAdjustments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete adjustment');
      }
    } catch (error) {
      console.error('Error deleting adjustment:', error);
      toast.error('Failed to delete adjustment');
    }
  };

  const handleCancelEdit = () => {
    if (existingAdjustment) {
      // Restore original values
      setFormData({
        extra_large_crates: existingAdjustment.extra_large_crates?.toString() || '0',
        extra_large_pieces: existingAdjustment.extra_large_pieces?.toString() || '0',
        extra_large_crack_crates: existingAdjustment.extra_large_crack_crates?.toString() || '0',
        extra_large_crack_pieces: existingAdjustment.extra_large_crack_pieces?.toString() || '0',
        large_crates: existingAdjustment.large_crates?.toString() || '0',
        large_pieces: existingAdjustment.large_pieces?.toString() || '0',
        large_crack_crates: existingAdjustment.large_crack_crates?.toString() || '0',
        large_crack_pieces: existingAdjustment.large_crack_pieces?.toString() || '0',
        medium_crates: existingAdjustment.medium_crates?.toString() || '0',
        medium_pieces: existingAdjustment.medium_pieces?.toString() || '0',
        medium_crack_crates: existingAdjustment.medium_crack_crates?.toString() || '0',
        medium_crack_pieces: existingAdjustment.medium_crack_pieces?.toString() || '0',
        small_crates: existingAdjustment.small_crates?.toString() || '0',
        small_pieces: existingAdjustment.small_pieces?.toString() || '0',
        small_crack_crates: existingAdjustment.small_crack_crates?.toString() || '0',
        small_crack_pieces: existingAdjustment.small_crack_pieces?.toString() || '0',
        damaged_crates: existingAdjustment.damaged_crates?.toString() || '0',
        damaged_pieces: existingAdjustment.damaged_pieces?.toString() || '0',
        reason: existingAdjustment.reason || ''
      });
    }
    setIsEditing(false);
  };

  // Check if a pen has adjustment for the selected date
  const penHasAdjustment = (penNumber) => {
    return allPenAdjustments.some(adj => adj.pen_number === penNumber);
  };

  // Get total adjustment for a pen
  const getPenAdjustmentSummary = (penNumber) => {
    const adj = allPenAdjustments.find(a => a.pen_number === penNumber);
    if (!adj) return null;
    
    const fields = [
      'extra_large_crates', 'extra_large_pieces', 'extra_large_crack_crates', 'extra_large_crack_pieces',
      'large_crates', 'large_pieces', 'large_crack_crates', 'large_crack_pieces',
      'medium_crates', 'medium_pieces', 'medium_crack_crates', 'medium_crack_pieces',
      'small_crates', 'small_pieces', 'small_crack_crates', 'small_crack_pieces',
      'damaged_crates', 'damaged_pieces'
    ];
    
    let positive = 0;
    let negative = 0;
    
    fields.forEach(f => {
      const val = adj[f] || 0;
      if (val > 0) positive += val;
      if (val < 0) negative += val;
    });
    
    return { positive, negative };
  };

  return (
    <div className="space-y-6" data-testid="productions-edit-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Productions Edit
        </h1>
        <p className="text-lg text-slate-600 mt-2">
          Adjust daily egg production figures (+/-) to correct the closing inventory
        </p>
      </div>

      {/* Alert Banner */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-amber-800">Important</p>
          <p className="text-sm text-amber-700">
            Enter positive numbers to add to production, negative numbers to subtract. 
            These adjustments directly affect the Daily Egg Closing Inventory calculations.
          </p>
        </div>
      </div>

      {/* Edit Mode Selection */}
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-500" />
            <Label className="text-sm font-medium">Date:</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-44"
              data-testid="date-picker"
            />
          </div>
          
          {/* Mode Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setEditMode('pen')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                editMode === 'pen' 
                  ? 'bg-white text-amber-700 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="mode-pen"
            >
              <Package className="w-4 h-4 inline mr-2" />
              Per Pen Edit
            </button>
            <button
              onClick={() => setEditMode('summary')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                editMode === 'summary' 
                  ? 'bg-white text-emerald-700 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="mode-summary"
            >
              <Egg className="w-4 h-4 inline mr-2" />
              Summary Edit
            </button>
            <button
              onClick={() => setEditMode('history')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                editMode === 'history' 
                  ? 'bg-white text-blue-700 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="mode-history"
            >
              <History className="w-4 h-4 inline mr-2" />
              History
            </button>
          </div>
          
          <Button
            variant="outline"
            onClick={() => { 
              if (editMode === 'pen') {
                fetchPenAdjustment(); 
              } else if (editMode === 'summary') {
                fetchSummaryAdjustment();
              } else {
                fetchAdjustmentHistory();
              }
              fetchAllPenAdjustments(); 
            }}
            disabled={loading || historyLoading}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${(loading || historyLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Per Pen Edit Mode */}
      {editMode === 'pen' && (
        <>
          {/* Pen Selection */}
          <div className="card">
            <div className="flex items-center gap-4">
              <Package className="w-5 h-5 text-slate-500" />
              <Label className="text-sm font-medium">Select Pen:</Label>
              <Select value={selectedPen} onValueChange={setSelectedPen}>
                <SelectTrigger className="w-40" data-testid="pen-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pens.map(pen => (
                    <SelectItem key={pen} value={pen.toString()}>
                      <span className="flex items-center gap-2">
                        Pen {pen}
                        {penHasAdjustment(pen) && (
                          <span className="text-amber-500 text-xs">⚠ Adjusted</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status Banner */}
          {existingAdjustment && !isEditing && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Edit2 className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">Adjustment Recorded</p>
              <p className="text-sm text-amber-600">
                Adjusted by {existingAdjustment.created_by_name} on {new Date(existingAdjustment.created_at).toLocaleString()}
                {existingAdjustment.reason && (
                  <span className="ml-2">| Reason: {existingAdjustment.reason}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              data-testid="edit-button"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              className="text-red-600 hover:bg-red-50"
              data-testid="delete-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Adjustment Entry Form */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Egg className="w-5 h-5 text-amber-500" />
            Pen {selectedPen} Adjustment - {selectedDate}
          </h2>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                {existingAdjustment && (
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="save-adjustment-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Adjustment'}
                </Button>
              </>
            ) : (
              <span className="text-sm text-slate-500 italic">View mode - Click Edit to modify</span>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mb-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Plus className="w-4 h-4 text-green-600" />
              <span className="text-green-600">Positive values add</span> to closing inventory
            </span>
            <span className="flex items-center gap-1">
              <Minus className="w-4 h-4 text-red-600" />
              <span className="text-red-600">Negative values subtract</span> from closing inventory
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {eggSizes.map(size => (
            <AdjustmentInputCard 
              key={size.key} 
              size={size}
              formData={formData}
              handleInputChange={handleInputChange}
              isEditing={isEditing}
            />
          ))}
        </div>

        <div className="mt-4">
          <Label className="text-sm font-medium">Reason for Adjustment</Label>
          <Textarea
            value={formData.reason}
            onChange={(e) => handleInputChange('reason', e.target.value)}
            placeholder="Explain why this adjustment is needed (e.g., counting error, damaged eggs, etc.)..."
            className="mt-1"
            disabled={!isEditing}
            rows={2}
            data-testid="reason-input"
          />
        </div>
      </div>

      {/* Quick Overview - Which pens have adjustments for selected date */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-amber-500" />
          Adjustment Status for {selectedDate}
        </h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {pens.map(pen => {
            const hasAdjustment = penHasAdjustment(pen);
            const summary = getPenAdjustmentSummary(pen);
            const isSelected = selectedPen === pen.toString();
            
            return (
              <div
                key={pen}
                className={`p-3 rounded-lg border-2 text-center transition-all cursor-pointer ${
                  hasAdjustment 
                    ? 'bg-amber-50 border-amber-300 hover:border-amber-400' 
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                } ${isSelected ? 'ring-2 ring-amber-500' : ''}`}
                onClick={() => setSelectedPen(pen.toString())}
              >
                <div className="font-bold text-lg">Pen {pen}</div>
                <div className={`text-xs mt-1 ${
                  hasAdjustment ? 'text-amber-600' : 'text-slate-400'
                }`}>
                  {hasAdjustment ? (
                    <div className="space-y-0.5">
                      {summary?.positive > 0 && (
                        <div className="text-green-600">+{summary.positive}</div>
                      )}
                      {summary?.negative < 0 && (
                        <div className="text-red-600">{summary.negative}</div>
                      )}
                    </div>
                  ) : (
                    'No adjustment'
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
        </>
      )}

      {/* Summary Edit Mode */}
      {editMode === 'summary' && (
        <>
          {/* Summary Status Banner */}
          {summaryAdjustment && !isSummaryEditing && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Edit2 className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-800">Summary Adjustment Recorded</p>
                  <p className="text-sm text-emerald-600">
                    Adjusted by {summaryAdjustment.created_by_name} on {new Date(summaryAdjustment.created_at).toLocaleString()}
                    {summaryAdjustment.reason && (
                      <span className="ml-2">| Reason: {summaryAdjustment.reason}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSummaryEditing(true)}
                  data-testid="edit-summary-button"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteSummary}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="delete-summary-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}

          {/* Summary Adjustment Form */}
          <div className="card border-2 border-emerald-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                Daily Closing Inventory Adjustment - {selectedDate}
              </h2>
              <div className="flex items-center gap-2">
                {isSummaryEditing ? (
                  <>
                    {summaryAdjustment && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Restore original values
                          setSummaryFormData({
                            extra_large_crates: summaryAdjustment.extra_large_crates?.toString() || '0',
                            extra_large_pieces: summaryAdjustment.extra_large_pieces?.toString() || '0',
                            extra_large_crack_crates: summaryAdjustment.extra_large_crack_crates?.toString() || '0',
                            extra_large_crack_pieces: summaryAdjustment.extra_large_crack_pieces?.toString() || '0',
                            large_crates: summaryAdjustment.large_crates?.toString() || '0',
                            large_pieces: summaryAdjustment.large_pieces?.toString() || '0',
                            large_crack_crates: summaryAdjustment.large_crack_crates?.toString() || '0',
                            large_crack_pieces: summaryAdjustment.large_crack_pieces?.toString() || '0',
                            medium_crates: summaryAdjustment.medium_crates?.toString() || '0',
                            medium_pieces: summaryAdjustment.medium_pieces?.toString() || '0',
                            medium_crack_crates: summaryAdjustment.medium_crack_crates?.toString() || '0',
                            medium_crack_pieces: summaryAdjustment.medium_crack_pieces?.toString() || '0',
                            small_crates: summaryAdjustment.small_crates?.toString() || '0',
                            small_pieces: summaryAdjustment.small_pieces?.toString() || '0',
                            small_crack_crates: summaryAdjustment.small_crack_crates?.toString() || '0',
                            small_crack_pieces: summaryAdjustment.small_crack_pieces?.toString() || '0',
                            damaged_crates: summaryAdjustment.damaged_crates?.toString() || '0',
                            damaged_pieces: summaryAdjustment.damaged_pieces?.toString() || '0',
                            reason: summaryAdjustment.reason || ''
                          });
                          setIsSummaryEditing(false);
                        }}
                        data-testid="cancel-summary-button"
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      onClick={handleSaveSummary}
                      disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="save-summary-button"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Summary Adjustment'}
                    </Button>
                  </>
                ) : (
                  <span className="text-sm text-slate-500 italic">View mode - Click Edit to modify</span>
                )}
              </div>
            </div>

            {/* Summary Info Banner */}
            <div className="mb-4 p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700">
              <p className="font-medium mb-1">📊 Summary Adjustment</p>
              <p>
                This adjustment applies directly to the <strong>Daily Closing Inventory</strong> totals, 
                independent of individual pen adjustments. Use this for overall corrections that don't 
                relate to specific pens.
              </p>
            </div>

            {/* Instructions */}
            <div className="mb-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Plus className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">Positive values add</span> to closing inventory
                </span>
                <span className="flex items-center gap-1">
                  <Minus className="w-4 h-4 text-red-600" />
                  <span className="text-red-600">Negative values subtract</span> from closing inventory
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {eggSizes.map(size => (
                <AdjustmentInputCard 
                  key={size.key} 
                  size={size}
                  formData={summaryFormData}
                  handleInputChange={handleSummaryInputChange}
                  isEditing={isSummaryEditing}
                />
              ))}
            </div>

            <div className="mt-4">
              <Label className="text-sm font-medium">Reason for Summary Adjustment</Label>
              <Textarea
                value={summaryFormData.reason}
                onChange={(e) => handleSummaryInputChange('reason', e.target.value)}
                placeholder="Explain why this overall adjustment is needed (e.g., stock reconciliation, inventory audit correction, etc.)..."
                className="mt-1"
                disabled={!isSummaryEditing}
                rows={2}
                data-testid="summary-reason-input"
              />
            </div>
          </div>

          {/* Summary Adjustment Status */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-500" />
              Summary Adjustment Status for {selectedDate}
            </h3>
            <div className="p-4 rounded-lg border-2 text-center transition-all bg-emerald-50 border-emerald-200">
              {summaryAdjustment ? (
                <div>
                  <div className="font-bold text-lg text-emerald-700 mb-2">Summary Adjusted</div>
                  <div className="grid grid-cols-5 gap-2 text-xs">
                    {eggSizes.map(size => {
                      const crates = summaryAdjustment[`${size.key}_crates`] || 0;
                      const pieces = summaryAdjustment[`${size.key}_pieces`] || 0;
                      return (
                        <div key={size.key} className="p-2 bg-white rounded">
                          <div className="font-medium">{size.label}</div>
                          <div className={crates !== 0 ? (crates > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {crates > 0 ? '+' : ''}{crates}c
                          </div>
                          <div className={pieces !== 0 ? (pieces > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {pieces > 0 ? '+' : ''}{pieces}p
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-slate-500">No summary adjustment for this date</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* History Mode */}
      {editMode === 'history' && (
        <>
          {/* History Filters */}
          <div className="card">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-500" />
                <Label className="text-sm font-medium">From:</Label>
                <Input
                  type="date"
                  value={historyStartDate}
                  onChange={(e) => setHistoryStartDate(e.target.value)}
                  className="w-40"
                  data-testid="history-start-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">To:</Label>
                <Input
                  type="date"
                  value={historyEndDate}
                  onChange={(e) => setHistoryEndDate(e.target.value)}
                  className="w-40"
                  data-testid="history-end-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-500" />
                <Label className="text-sm font-medium">Type:</Label>
                <Select value={historyFilter} onValueChange={setHistoryFilter}>
                  <SelectTrigger className="w-40" data-testid="history-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Adjustments</SelectItem>
                    <SelectItem value="pen">Per Pen Only</SelectItem>
                    <SelectItem value="summary">Summary Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto text-sm text-slate-500">
                {historyData.length} adjustment{historyData.length !== 1 ? 's' : ''} found
              </div>
            </div>
          </div>

          {/* History Table */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Adjustment History Report
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Export to CSV
                  const headers = ['Date', 'Type', 'Adjusted By', 'Reason', 'XL Crates', 'XL Pieces', 'L Crates', 'L Pieces', 'M Crates', 'M Pieces', 'S Crates', 'S Pieces', 'Damaged Crates', 'Damaged Pieces', 'Created At'];
                  const rows = historyData.map(adj => [
                    adj.date,
                    adj.pen_number === 0 ? 'Summary' : `Pen ${adj.pen_number}`,
                    adj.created_by_name || 'Unknown',
                    adj.reason || '',
                    adj.extra_large_crates || 0,
                    adj.extra_large_pieces || 0,
                    adj.large_crates || 0,
                    adj.large_pieces || 0,
                    adj.medium_crates || 0,
                    adj.medium_pieces || 0,
                    adj.small_crates || 0,
                    adj.small_pieces || 0,
                    adj.damaged_crates || 0,
                    adj.damaged_pieces || 0,
                    adj.created_at ? new Date(adj.created_at).toLocaleString() : ''
                  ]);
                  
                  const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `adjustment_history_${historyStartDate}_to_${historyEndDate}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('History exported to CSV');
                }}
                data-testid="export-history-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {historyLoading ? (
              <div className="text-center py-8 text-slate-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-500" />
                <p>Loading history...</p>
              </div>
            ) : historyData.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p className="font-medium">No adjustments found</p>
                <p className="text-sm">Try adjusting the date range or filter</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 font-semibold">Date</th>
                      <th className="text-left py-3 px-4 font-semibold">Type</th>
                      <th className="text-left py-3 px-4 font-semibold">Adjusted By</th>
                      <th className="text-left py-3 px-4 font-semibold">XL</th>
                      <th className="text-left py-3 px-4 font-semibold">Large</th>
                      <th className="text-left py-3 px-4 font-semibold">Medium</th>
                      <th className="text-left py-3 px-4 font-semibold">Small</th>
                      <th className="text-left py-3 px-4 font-semibold">Damaged</th>
                      <th className="text-left py-3 px-4 font-semibold">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((adj, idx) => (
                      <tr key={adj.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium">{adj.date}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            adj.pen_number === 0 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {adj.pen_number === 0 ? 'Summary' : `Pen ${adj.pen_number}`}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-600">
                          <div>{adj.created_by_name || 'Unknown'}</div>
                          <div className="text-xs text-slate-400">
                            {adj.created_at ? new Date(adj.created_at).toLocaleString() : ''}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={(adj.extra_large_crates || 0) !== 0 ? ((adj.extra_large_crates || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.extra_large_crates || 0) > 0 ? '+' : ''}{adj.extra_large_crates || 0}c
                          </span>
                          <span className="text-slate-300 mx-1">/</span>
                          <span className={(adj.extra_large_pieces || 0) !== 0 ? ((adj.extra_large_pieces || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.extra_large_pieces || 0) > 0 ? '+' : ''}{adj.extra_large_pieces || 0}p
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={(adj.large_crates || 0) !== 0 ? ((adj.large_crates || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.large_crates || 0) > 0 ? '+' : ''}{adj.large_crates || 0}c
                          </span>
                          <span className="text-slate-300 mx-1">/</span>
                          <span className={(adj.large_pieces || 0) !== 0 ? ((adj.large_pieces || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.large_pieces || 0) > 0 ? '+' : ''}{adj.large_pieces || 0}p
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={(adj.medium_crates || 0) !== 0 ? ((adj.medium_crates || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.medium_crates || 0) > 0 ? '+' : ''}{adj.medium_crates || 0}c
                          </span>
                          <span className="text-slate-300 mx-1">/</span>
                          <span className={(adj.medium_pieces || 0) !== 0 ? ((adj.medium_pieces || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.medium_pieces || 0) > 0 ? '+' : ''}{adj.medium_pieces || 0}p
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={(adj.small_crates || 0) !== 0 ? ((adj.small_crates || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.small_crates || 0) > 0 ? '+' : ''}{adj.small_crates || 0}c
                          </span>
                          <span className="text-slate-300 mx-1">/</span>
                          <span className={(adj.small_pieces || 0) !== 0 ? ((adj.small_pieces || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.small_pieces || 0) > 0 ? '+' : ''}{adj.small_pieces || 0}p
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={(adj.damaged_crates || 0) !== 0 ? ((adj.damaged_crates || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.damaged_crates || 0) > 0 ? '+' : ''}{adj.damaged_crates || 0}c
                          </span>
                          <span className="text-slate-300 mx-1">/</span>
                          <span className={(adj.damaged_pieces || 0) !== 0 ? ((adj.damaged_pieces || 0) > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}>
                            {(adj.damaged_pieces || 0) > 0 ? '+' : ''}{adj.damaged_pieces || 0}p
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-600 max-w-xs truncate" title={adj.reason || ''}>
                          {adj.reason || <span className="text-slate-400 italic">No reason</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductionsEdit;
